import { Button, useIsFeatureEnabled, SplitButton } from '@innovyze/stylovyze';
import { Edit, Delete as DeleteIcon } from '@mui/icons-material';
import { useGlobalization } from '@Translations';
import { FLAGS } from '@Types/flags';
import { addRouteNamespace } from '@Utils';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MenuGrid } from './EditSensor.styles';
import { SpinnerStatusIndicator } from './SpinnerStatusIndicator';

interface ControlButtonsProps {
	renderTest: boolean;
	disableTest: boolean;
	showResample: boolean;
	showResamplingJob: boolean;
	disableResample: boolean;
	disableMultiaction: boolean;
	disableEditSensorData?: boolean;
	disableDeleteSensor?: boolean;
	onClickTestConnButton: () => void;
	onClickResampleButton: () => void;
	onClickSaveButton: () => void;
	onClickDeleteButton: () => void;
}

const ControlsButtons: React.FC<ControlButtonsProps> = (
	props: ControlButtonsProps,
) => {
	const {
		renderTest,
		disableTest,
		showResample,
		showResamplingJob,
		disableResample,
		disableMultiaction,
		disableEditSensorData,
		disableDeleteSensor,
		onClickTestConnButton,
		onClickResampleButton,
		onClickSaveButton,
		onClickDeleteButton,
	}: ControlButtonsProps = props;
	const { sensorId } = useParams<{ sensorId: string }>();
	const { t } = useGlobalization();
	const history = useHistory();
	const handleEditSensorNavigation = () => {
		history.push(addRouteNamespace(`sensors/${sensorId}/edit/data`));
	};

	const isSensorDataEnabled = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		FLAGS.EDIT_SENSORS_DATA,
	);

	const splitButtonsOptions = [
		{
			title: t('Save Configuration'),
			onClick: onClickSaveButton,
			cy: 'SaveConfigurationButton',
			key: 0,
			icon: <></>,
			disabled: false,
		},
	];
	if (isSensorDataEnabled) {
		splitButtonsOptions.push({
			title: t('Edit Sensor Data'),
			onClick: handleEditSensorNavigation,
			cy: 'editSensorData',
			icon: <Edit data-cy="editSensorData" />,
			key: 0,
			disabled: !!disableEditSensorData,
		});
	}

	splitButtonsOptions.push({
		title: t('Delete'),
		onClick: onClickDeleteButton,
		cy: 'deleteConfiguration',
		key: 1,
		icon: <DeleteIcon data-cy="deleteConfiguration" />,
		disabled: !!disableDeleteSensor,
	});

	return (
		<MenuGrid>
			{showResamplingJob && (
				<SpinnerStatusIndicator
					visible={showResamplingJob}
					text={t('Issuing resample job')}
				/>
			)}
			{renderTest && (
				<Button
					variant="outlined"
					color={
						disableTest || disableResample ? 'secondary' : 'primary'
					}
					onClick={onClickTestConnButton}
					disabled={disableTest || disableResample}
					data-test-id="test-conn-action-button">
					{t('Test')}
				</Button>
			)}
			{showResample && (
				<Button
					variant="outlined"
					color={disableResample ? 'secondary' : 'primary'}
					onClick={onClickResampleButton}
					disabled={disableResample}
					data-test-id="resample-action-button">
					{t('Resample Sensor')}
				</Button>
			)}
			<SplitButton
				disabled={disableMultiaction}
				options={splitButtonsOptions}
				oneClickMenu
				includeSelectedInMenu={false}
			/>
		</MenuGrid>
	);
};

export default ControlsButtons;
